import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
    ChevronLeft,
    Send,
    Trash2,
    Plus,
    GripVertical,
    Clock,
    DollarSign,
    Users,
    Store,
    Tag
} from 'lucide-react';
import { adminCategoriesService, adminPlansService, adminVendorCategoriesService } from '../services';
import { formatPriceDisplay } from '../utils/formatPrice';
import './PlanDetail.css';

const getDefaultNewPlan = (firstCategoryName = '') => ({
    id: 'new',
    title: '',
    thumbnail: '',
    category: firstCategoryName,
    location: '',
    description: '',
    price: 0,
    duration: 0,
    capacity: 2,
    bookings: 0,
    status: 'Draft',
    isPublic: false,
    createdDate: new Date().toISOString().split('T')[0],
    activities: [],
    slots: [],
});

const PlanDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [plan, setPlan] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    const [vendorCategories, setVendorCategories] = useState([]);
    const [toast, setToast] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        adminCategoriesService.list().then((list) => setCategories(list || [])).catch(() => {});
        adminVendorCategoriesService.list().then((list) => setVendorCategories(list || [])).catch(() => {});
    }, []);

    useEffect(() => {
        // New plan
        if (id === 'new') {
            setPlan(getDefaultNewPlan(''));
            return;
        }

        // Always fetch from API so category, location, and activities load from DB.
        (async () => {
            try {
                const data = await adminPlansService.getById(id);
                setPlan({
                    ...data,
                    category: data.category ?? data.adminCategory ?? '',
                    location: data.location ?? data.adminLocation ?? '',
                    activities: Array.isArray(data.activities) ? data.activities.map(act => ({
                        ...act,
                        title: act.name || act.title || '',
                    })) : [],
                });
            } catch (err) {
                console.error('Failed to load plan:', err);
                navigate('/plans');
            }
        })();
    }, [id, navigate]);

    if (!plan) {
        return (
            <div className="plan-detail-loading">
                <div className="loading-spinner" />
                <span>Loading plan…</span>
            </div>
        );
    }

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Validate required fields
            if (!plan.title || !plan.title.trim()) {
                showToast('Please enter a plan title', 'error');
                setIsSaving(false);
                return;
            }
            if (!plan.category) {
                showToast('Please select a category', 'error');
                setIsSaving(false);
                return;
            }
            if (plan.activities.length < 2) {
                showToast('Please add at least 2 activities', 'error');
                setIsSaving(false);
                return;
            }

            for (let i = 0; i < plan.activities.length; i++) {
                const act = plan.activities[i];
                const slot = `Activity ${i + 1}`;
                if (!act.title || !act.title.trim()) {
                    showToast(`${slot}: please enter an activity name`, 'error');
                    setIsSaving(false);
                    return;
                }
                if (!act.vendorCategoryId) {
                    showToast(`${slot}: please select an activity type`, 'error');
                    setIsSaving(false);
                    return;
                }
            }

            // Prepare data for API
            const planData = {
                title: plan.title,
                description: plan.description || '',
                thumbnail: plan.thumbnail || null,
                category: plan.category,
                location: plan.location || null,
                capacity: plan.capacity || 2,
                activities: plan.activities.map((act, idx) => ({
                    name: act.title || '',
                    vendorCategoryId: act.vendorCategoryId || null,
                    vendorIds: act.vendorIds || [],
                    duration: act.duration || 60,
                    price: act.price || 0,
                    time: act.time || '',
                    endTime: act.endTime || '',
                    location: act.location || '',
                    description: act.title || act.description || '',
                    imageUrl: act.imageUrl || null,
                    order: idx
                }))
            };

            // Create or update plan
            if (id === 'new') {
                await adminPlansService.create(planData);
                showToast('Plan published successfully!', 'success');
            } else {
                await adminPlansService.update(id, planData);
                showToast('Plan updated successfully!', 'success');
            }
            navigate('/plans');
        } catch (error) {
            console.error('Failed to save plan:', error);
            const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Unknown error';
            showToast(`Failed to publish plan: ${errorMessage}`, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeletePlan = async () => {
        if (!id || id === 'new') return;
        setShowDeleteConfirm(true);
    };

    const confirmDeletePlan = async () => {
        if (!id || id === 'new') {
            setShowDeleteConfirm(false);
            return;
        }
        try {
            await adminPlansService.delete(id);
            setShowDeleteConfirm(false);
            showToast('Plan deleted successfully', 'success');
            navigate('/plans');
        } catch (error) {
            console.error('Failed to delete plan:', error);
            const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Unknown error';
            showToast(`Failed to delete plan: ${errorMessage}`, 'error');
            setShowDeleteConfirm(false);
        }
    };

    const handleAddActivity = () => {
        if (plan.activities.length >= 4) return;

        const newActivity = {
            id: Date.now(),
            title: '',
            vendorIds: [],
            type: '', // display label (vendor category name)
            vendorCategoryId: '',
            duration: 60,
            price: 0,
            time: '',
            endTime: '',
            location: '',
            description: '',
            imageUrl: '',
            order: plan.activities.length + 1
        };

        setPlan({
            ...plan,
            activities: [...plan.activities, newActivity]
        });
    };

    const handleRemoveActivity = (id) => {
        if (plan.activities.length <= 2) return;
        setPlan({
            ...plan,
            activities: plan.activities.filter(a => a.id !== id)
        });
    };

    const handleActivityChange = (id, field, value) => {
        const updatedActivities = plan.activities.map(a => {
            if (a.id === id) {
                if (field === 'vendorCategoryId') {
                    const selected = (vendorCategories || []).find((c) => String(c.id) === String(value));
                    // Reset vendor selection if vendor category changes
                    return {
                        ...a,
                        vendorCategoryId: value,
                        type: selected?.name || '',
                        vendorIds: [],
                    };
                }
                if (field === 'toggleVendor') {
                    const vendorId = typeof value === 'number' ? value : (parseInt(value, 10) || value);
                    const currentIds = a.vendorIds || [];
                    const has = currentIds.some((vid) => vid == vendorId);
                    const newIds = has ? currentIds.filter((vid) => vid != vendorId) : [...currentIds, vendorId];
                    return { ...a, vendorIds: newIds };
                }
                return { ...a, [field]: value };
            }
            return a;
        });

        // Recalculate total price and duration
        const totalPrice = updatedActivities.reduce((sum, a) => sum + (parseFloat(a.price) || 0), 0);
        const totalDuration = updatedActivities.reduce((sum, a) => sum + (parseInt(a.duration) || 0), 0);

        setPlan({
            ...plan,
            activities: updatedActivities,
            price: totalPrice,
            duration: totalDuration
        });
    };

    return (
        <div className="plan-detail-container">
            <div className="page-header">
                <div className="header-titles">
                    <Link to="/plans" className="back-link">
                        <ChevronLeft size={18} /> Back to Plans
                    </Link>
                    <h1>{plan.id === 'new' ? 'Create New Plan' : plan.title}</h1>
                    <p>Manage activities, vendors, and visibility for this hangout plan.</p>
                </div>
                <div className="header-actions">
                    {id !== 'new' && (
                        <button
                            className="btn-secondary danger-btn"
                            type="button"
                            onClick={handleDeletePlan}
                            disabled={isSaving}
                        >
                            <Trash2 size={16} />
                            Delete
                        </button>
                    )}
                    <button className="btn-primary save-btn" onClick={handleSave} disabled={isSaving}>
                        <Send size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                        {isSaving
                            ? (id === 'new' ? 'Creating...' : 'Updating...')
                            : (id === 'new' ? 'Publish Plan' : 'Update Plan')}
                    </button>
                </div>
            </div>

            <div className="plan-grid-layout">
                <div className="main-config">
                    <div className="card">
                        <h3 className="card-title">General Information</h3>
                        <div className="form-group">
                            <label>Plan Thumbnail</label>
                            <div style={{
                                border: '2px dashed #d0d0d0',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                textAlign: 'center',
                                backgroundColor: '#fafafa',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                position: 'relative'
                            }}>
                                {!plan.thumbnail ? (
                                    <label style={{ cursor: 'pointer', display: 'block' }}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                        setPlan({ ...plan, thumbnail: reader.result });
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            padding: '2rem 1rem'
                                        }}>
                                            <div style={{
                                                width: '64px',
                                                height: '64px',
                                                borderRadius: '50%',
                                                backgroundColor: 'var(--primary-color)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white'
                                            }}>
                                                <Plus size={32} />
                                            </div>
                                            <div>
                                                <p style={{ margin: 0, fontWeight: '600', color: '#333', fontSize: '1rem' }}>
                                                    Upload Plan Thumbnail
                                                </p>
                                                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#666' }}>
                                                    Click to browse or drag and drop
                                                </p>
                                                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#999' }}>
                                                    PNG, JPG or WEBP (Max. 5MB)
                                                </p>
                                            </div>
                                        </div>
                                    </label>
                                ) : (
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <img 
                                            src={plan.thumbnail} 
                                            alt="Plan thumbnail" 
                                            style={{ 
                                                maxWidth: '100%',
                                                maxHeight: '300px',
                                                borderRadius: '8px',
                                                objectFit: 'cover'
                                            }} 
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setPlan({ ...plan, thumbnail: '' })}
                                            style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                background: 'rgba(0, 0, 0, 0.7)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '36px',
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)'}
                                            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Plan Title</label>
                            <input
                                type="text"
                                className="form-input"
                                value={plan.title}
                                onChange={(e) => setPlan({ ...plan, title: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                className="form-textarea"
                                value={plan.description}
                                onChange={(e) => setPlan({ ...plan, description: e.target.value })}
                            />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    className="form-select"
                                    value={plan.category ?? ''}
                                    onChange={(e) => setPlan({ ...plan, category: e.target.value })}
                                >
                                    <option value="">Select category</option>
                                    {categories.filter(c => c.status === 'active').map(cat => (
                                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Capacity (People)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={plan.capacity}
                                    onChange={(e) => setPlan({ ...plan, capacity: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Location</label>
                            <select
                                className="form-select"
                                value={plan.location ?? ''}
                                onChange={(e) => setPlan({ ...plan, location: e.target.value })}
                            >
                                <option value="">Select location</option>
                                <option value="New Cairo">New Cairo</option>
                                <option value="Sheikh Zayed">Sheikh Zayed</option>
                                <option value="Maadi">Maadi</option>
                            </select>
                        </div>
                    </div>

                    <div className="card activities-card">
                        <div className="card-header-flex">
                            <h3 className="card-title">Hangout Journey (Activities)</h3>
                            <button
                                className="add-activity-button"
                                onClick={handleAddActivity}
                                disabled={plan.activities.length >= 4}
                            >
                                <Plus size={16} /> Add Activity Slot
                            </button>
                        </div>

                        <div className="activities-list-detail">
                            {plan.activities.length === 0 && (
                                <div className="empty-activities">
                                    No activities added yet. Add between 2 and 4 activities to create a journey.
                                </div>
                            )}
                            {plan.activities.map((activity, index) => (
                                <ActivitySlot
                                    key={activity.id}
                                    index={index}
                                    activity={activity}
                                    onChange={handleActivityChange}
                                    onRemove={handleRemoveActivity}
                                    canRemove={plan.activities.length > 2}
                                    vendorCategories={vendorCategories}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="side-stats">
                    <div className="card stats-card">
                        <h3 className="card-title">Plan Pricing</h3>
                        <div className="summary-item">
                            <div className="summary-icon"><DollarSign size={20} /></div>
                            <div className="summary-info">
                                <span className="summary-label">Total Plan Price</span>
                                <span className="summary-value">{formatPriceDisplay(plan.price)} EGP</span>
                            </div>
                        </div>
                        <div className="summary-divider"></div>
                        <small style={{ color: '#666', fontSize: '0.875rem', display: 'block', marginTop: '0.5rem' }}>
                            Sum of all activities prices
                        </small>
                    </div>
                </div>
            </div>

            {toast && (
                <div className={`toast ${toast.type}`}>
                    {toast.message}
                </div>
            )}

            {showDeleteConfirm && (
                <div className="confirm-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="confirm-title">Delete this plan?</h3>
                        <p className="confirm-text">
                            This action cannot be undone. The plan and its activities will be removed from the dashboard.
                        </p>
                        <div className="confirm-actions">
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-secondary danger-btn"
                                onClick={confirmDeletePlan}
                            >
                                <Trash2 size={16} />
                                Delete Plan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const MultiSelectDropdown = ({ options, selectedIds, onToggle, placeholder, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = React.useRef(null);
    const isSelected = (id) => (selectedIds || []).some((sid) => sid == id); // loose eq for string/number IDs

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedVendors = options.filter((opt) => isSelected(opt.id));

    return (
        <div className={`multi-select-dropdown ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
            <div
                className={`dropdown-field ${isOpen ? 'active' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <div className="selected-values">
                    {selectedVendors.length === 0 ? (
                        <span className="placeholder">{placeholder}</span>
                    ) : (
                        <div className="chips-container">
                            {selectedVendors.map(v => (
                                <span key={v.id} className="vendor-chip">
                                    {v.name}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="dropdown-arrow">
                    <Plus size={16} className={isOpen ? 'rotate' : ''} />
                </div>
            </div>

            {isOpen && (
                <div className="dropdown-menu">
                    {options.length === 0 ? (
                        <div className="no-options">No vendors found</div>
                    ) : (
                        options.map(opt => (
                            <label key={opt.id} className="dropdown-item">
                                <input
                                    type="checkbox"
                                    checked={isSelected(opt.id)}
                                    onChange={() => onToggle(opt.id)}
                                />
                                <div className="item-info">
                                    <span className="item-name">{opt.name}</span>
                                    <span className="item-meta">{opt.location}</span>
                                </div>
                            </label>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

const ActivitySlot = ({ activity, index, onChange, onRemove, canRemove, vendorCategories }) => {
    return (
        <div className="activity-slot-item">
            <div className="slot-header">
                <div className="slot-index">{index + 1}</div>
                <div className="slot-title">Activity Slot {index + 1}</div>
                <button className="remove-slot" onClick={() => onRemove(activity.id)} disabled={!canRemove}>
                    <Trash2 size={16} />
                </button>
            </div>

            <div className="slot-body">
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label className="slot-label">Activity Title</label>
                    <input
                        type="text"
                        className="form-input condensed"
                        placeholder="Enter activity title"
                        value={activity.title || ''}
                        onChange={(e) => onChange(activity.id, 'title', e.target.value)}
                    />
                </div>

                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label className="slot-label">Vendor category</label>
                    <select
                        className="form-select condensed"
                        value={activity.vendorCategoryId || ''}
                        onChange={(e) => onChange(activity.id, 'vendorCategoryId', e.target.value)}
                    >
                        <option value="">Select Type</option>
                        {(vendorCategories || []).map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                </div>

            </div>
        </div>
    );
};

export default PlanDetail;
